import { useState, ReactNode, useReducer } from 'react';
import { Mark, EditPositionType } from '..';
import AssetInput from '../../../../../components/AssetInput';
import LeverageSlider from '../../../../../components/LeverageSlider';
import PercentageSelector from '../../../../../components/PercentageSelector';
import {
    INSUFFICIENT_BALANCE,
    Tokens,
    OrderBookType,
    Validators,
    OrderDataTypes,
    EnabledChains,
} from '../../../../../config/constants';
import useModalState from '../../../../../context/modal/useModalState';
import { formatUSD, leverageMultiplier, verifyChainConfig } from '../../../../../helpers';
import { UserPositionRaw } from '../../../../../hooks/web3/trade/useUserPositions';
import { preventOverflow } from '../../../../../web3/utils';
import useUserBalance from '../../../../../hooks/web3/useUserBalance';
import { config } from '../../../../../config/config';
import useTokenDetails from '../../../../../hooks/web3/useTokenDetails';
import { formatEther, formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { useWeb3React } from '@web3-react/core';
import { BigNumber } from 'ethers';
import { ExtendPositionParamsStruct } from '../../../../../config/contracts/typechain/ITradeManager';
import LimitPriceDropdown from '../../LimitPriceDropdown';
import SizePreview from './SizePreview';
import calculateBankruptcyPrice from '../../../../../helpers/calculations/calculateBankruptcyPrice';
import { UserOrder } from '../../../../../hooks/web3/trade/useUserOpenOrders';
import useCancelOrder from '../../../../../hooks/web3/trade/useCancelOrder';
import useCreateOrder from '../../../../../hooks/web3/trade/useCreateOrder';
import useApproveToken from '../../../../../hooks/web3/useApproveToken';

export type EditPositionSizeProps = {
    marks: Mark[];
    children(confirm: {
        confirm: () => Promise<void>;
        validators: Validators[];
        isApproved: boolean;
        approve: (approveAmount: string) => Promise<void>;
    }): ReactNode;
    setOrderCancelled: React.Dispatch<React.SetStateAction<boolean>>;
    orderCancelled: boolean;
};

const EditPositionSize: React.FC<EditPositionSizeProps> = ({
    marks,
    orderCancelled,
    setOrderCancelled,
    children,
}) => {
    const { modalData } = useModalState();
    const { chainId, account } = useWeb3React();

    const cancel = useCancelOrder();
    const createOrder = useCreateOrder();

    const userPosition: UserPositionRaw | UserOrder | undefined = modalData.data.position;
    const userOrder =
        userPosition &&
        'orderData' in userPosition &&
        modalData.data.defaultEditTab === EditPositionType.SIZE
            ? userPosition
            : undefined;

    const userBalance = useUserBalance(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );
    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const [amount, setAmount] = useState(
        userOrder?.orderData
            ? formatUnits(
                  (
                      userOrder.orderData as OrderDataTypes<ExtendPositionParamsStruct>
                  ).params.addedMargin.toString(),
                  decimals
              )
            : ''
    );
    const [leverage, setLeverage] = useState(
        userOrder?.orderData
            ? BigNumber.from(
                  (userOrder.orderData as OrderDataTypes<ExtendPositionParamsStruct>)
                      .params.addedLeverage
              )
                  .div(config.leverageMultiplier)
                  .toString()
            : config.minLeverage.toFixed(2)
    );
    const [isLimitOrder, toggleIsLimitOrder] = useReducer(
        (prev) => !prev,
        !!userOrder?.limitPrice
    );
    const [limitPrice, setLimitPrice] = useState(userOrder?.limitPrice || '');
    const [withExpiryDate, toggleWithExpiryDate] = useReducer(
        (prev) => !prev,
        !!userOrder?.orderData.constraints.deadline
    );
    const [expiryDate, setExpiryDate] = useState<Date | undefined>(
        userOrder
            ? new Date(Number(userOrder.orderData.constraints.deadline) * 1000)
            : undefined
    );

    const { isApproved, approve } = useApproveToken(
        config.addresses[chainId as EnabledChains[number]]?.collateral,
        config.addresses[chainId as EnabledChains[number]]?.tradeManager,
        amount
    );

    const validators: Validators[] = [
        {
            errorCondition: !amount,
            message: 'Amount is required!',
        },
        {
            errorCondition: parseUnits(amount || '0', decimals).gt(userBalance || '0'),
            message: INSUFFICIENT_BALANCE,
        },
        {
            errorCondition: parseEther(leverage || '0').lt(parseEther('1.1')),
            message: 'Minimum Leverage required is 1.1x!',
        },
        {
            errorCondition:
                isLimitOrder && (!limitPrice || parseEther(limitPrice).isZero()),
            message: 'Limit Price is required!',
        },
        {
            errorCondition: withExpiryDate && !expiryDate,
            message: 'Expiry Date is required!',
        },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setAmount(preventOverflow(value, decimals));
    };

    const handleMax = () => {
        setAmount(
            formatUSD(formatUnits(userBalance || '0', decimals), {
                useGrouping: false,
                currency: '',
                maxDecimals: decimals,
            })
        );
    };

    const getBankruptcyPrice = (latestPrice: string) => {
        const addedSize = parseEther(amount || '0').mul(parseEther(leverage || '0'));
        const newSize = parseEther(userPosition?.volume || '0').add(
            addedSize.div(parseEther('1'))
        );
        const newCollateral = parseEther(userPosition?.margin || '0').add(
            parseEther(amount || '0')
        );

        const newLeverage = newSize.mul(parseEther('1')).div(newCollateral);
        const newEntryPrice = newSize
            .mul(parseEther('1'))
            .div(
                parseEther(userPosition?.size || '0').add(
                    addedSize.div(parseEther(latestPrice))
                )
            );

        return calculateBankruptcyPrice(
            formatEther(newEntryPrice),
            userPosition?.isShort,
            formatEther(newLeverage)
        );
    };

    const confirm = async () => {
        if (!userPosition) throw new Error('UserPosition is undefined');
        if (!account) throw new Error('No wallet connected');
        if (!verifyChainConfig(chainId)) throw new Error('Failed to verify chainID');

        if (userOrder && !orderCancelled) {
            await cancel(userOrder.orderId, userOrder.orderType, () =>
                setOrderCancelled(true)
            );
            return;
        }

        await createOrder(
            OrderBookType.EXTEND,
            {
                addedLeverage: leverageMultiplier(leverage).toString(),
                addedMargin: parseUnits(amount, decimals).toString(),
                positionId: userPosition.id,
                tradePair: userPosition.tradePair.address,
            },
            'ExtendPositionOrder',
            userPosition.isShort,
            getBankruptcyPrice,
            {
                expiryDate: withExpiryDate ? expiryDate : undefined,
                limitPrice: isLimitOrder ? limitPrice : undefined,
                isMarketOrder: !limitPrice ? true : undefined,
                tradePair: userPosition.tradePair,
                userPosition,
            }
        );
    };

    return (
        <>
            <span className='position-type-text'>Increase the size of your position</span>
            <div className='collateral-container'>
                <AssetInput
                    label='Collateral'
                    sideLabel={
                        <>
                            Balance:{' '}
                            {formatUSD(formatUnits(userBalance || '0', decimals), {
                                currency: '',
                            })}{' '}
                            USDC
                        </>
                    }
                    name='amount'
                    value={amount}
                    onChange={handleChange}
                    asset={Tokens.USDC}
                    onMax={handleMax}
                    disabled={!!userOrder && !orderCancelled}
                />{' '}
                <PercentageSelector
                    currentValue={amount}
                    totalValue={formatUnits(userBalance || '0', decimals)}
                    handleChange={(value) =>
                        userOrder && !orderCancelled
                            ? undefined
                            : setAmount(preventOverflow(value, decimals))
                    }
                    disableButtons={!!userOrder && !orderCancelled}
                    decimals={decimals}
                />
            </div>
            <LeverageSlider
                value={leverage}
                minLeverage={parseFloat(
                    userPosition?.tradePair?.minLeverage || config.minLeverage.toString()
                ).toFixed(2)}
                onChange={setLeverage}
                marks={marks}
                disabled={!!userOrder && !orderCancelled}
            />
            <LimitPriceDropdown
                selectedTradePair={userPosition?.tradePair}
                limitPriceChecked={isLimitOrder}
                toggleLimitPrice={toggleIsLimitOrder}
                orderExpiryChecked={withExpiryDate}
                toggleOrderExpiry={toggleWithExpiryDate}
                amount={limitPrice}
                onChange={(value) => setLimitPrice(value)}
                date={expiryDate}
                handleDateChange={setExpiryDate}
                disabled={!!userOrder && !orderCancelled}
            />
            <SizePreview
                amount={amount}
                leverage={leverage}
                userPosition={userPosition}
            />
            {children({ confirm, validators, isApproved, approve })}
        </>
    );
};

export default EditPositionSize;
